// Shared by rebuy-search.js and rebuy-product-tabs.js: Rebuy key + rendering products with the collection card.
// Plain script (no import/export): everything is put on window.RebuyShared. Load it before those two files.
window.RebuyShared = window.RebuyShared || (() => {
  const shopRoot = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
  const searchPath = `${shopRoot}/search`;

  const handleFromProduct = (product) => {
    if (!product) return null;
    if (product.handle) return product.handle;
    // quick-view results have no handle, only url
    const match = String(product.url || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  let apiKeyPromise;
  // public key comes from rebuy.js (Rebuy.shop.api_key), wait for it to load.
  // a failed lookup isn't cached, so the next call tries again (e.g. consent given later)
  const getApiKey = () => {
    if (!apiKeyPromise) {
      apiKeyPromise = (async () => {
        for (let i = 0; i < 50; i += 1) {
          const shop = window.Rebuy && window.Rebuy.shop;
          if (shop && shop.api_key) return shop.api_key;
          if (window.__rebuyConfigFetch) {
            const rebuyConfig = await window.__rebuyConfigFetch;
            const key = rebuyConfig && rebuyConfig.data && rebuyConfig.data.shop && rebuyConfig.data.shop.api_key;
            if (key) return key;
          }
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        throw new Error('Rebuy API key not found');
      })().catch((error) => {
        apiKeyPromise = null;
        throw error;
      });
    }
    return apiKeyPromise;
  };

  const CARDS_PER_REQUEST = 40;
  const cardCache = new Map();
  const parser = new DOMParser();

  const cardHandle = (card) => {
    const fav = card.querySelector('[data-product-handle]');
    if (fav) return fav.dataset.productHandle;
    const link = card.querySelector('a.image-holder');
    return link ? handleFromProduct({ url: link.getAttribute('href') }) : null;
  };

  // one request for many cards: shopify search by handle, rendered by sections/rebuy-search-grid.liquid.
  // products that aren't sold in this market just don't come back -> cached as '' and skipped
  const loadCards = async (handles) => {
    const missing = handles.filter((handle) => !cardCache.has(handle));

    for (let i = 0; i < missing.length; i += CARDS_PER_REQUEST) {
      const chunk = missing.slice(i, i + CARDS_PER_REQUEST);
      const request = (async () => {
        const params = new URLSearchParams({
          type: 'product',
          q: chunk.map((handle) => `handle:${handle}`).join(' OR '),
          section_id: 'rebuy-search-grid',
        });
        const found = new Map();
        try {
          const response = await fetch(`${searchPath}?${params}`);
          if (response.ok) {
            const html = await response.text();
            parser.parseFromString(html, 'text/html').querySelectorAll('.product-button').forEach((card) => {
              const handle = cardHandle(card);
              if (handle) found.set(handle, card.outerHTML);
            });
          }
        } catch (error) {
          console.warn('[rebuy] cards failed', error);
        }
        return found;
      })();
      chunk.forEach((handle) => cardCache.set(handle, request.then((found) => found.get(handle) || '')));
    }

    return Promise.all(handles.map((handle) => cardCache.get(handle)));
  };

  const renderSkeletons = (container, count) => {
    container.innerHTML = '';
    for (let i = 0; i < count; i += 1) {
      const skeleton = document.createElement('div');
      skeleton.className = 'rs-card-skeleton';
      skeleton.innerHTML = '<span></span><i></i><i></i>';
      container.appendChild(skeleton);
    }
  };

  // cards in Rebuy's order. with limit: fill `limit` slots, extra products are backups.
  // returns how many cards were rendered
  const renderCards = async (container, products, { append = false, limit = 0 } = {}) => {
    const handles = [...new Set(products.map(handleFromProduct).filter(Boolean))];
    if (!append) container.innerHTML = '';

    const slots = handles.slice(0, limit || handles.length).map(() => {
      const slot = document.createElement('div');
      slot.className = 'rs-card-skeleton';
      slot.innerHTML = '<span></span><i></i><i></i>';
      container.appendChild(slot);
      return slot;
    });

    const cards = (await loadCards(handles)).filter(Boolean);
    let rendered = 0;
    slots.forEach((slot, index) => {
      if (!slot.isConnected) return;
      if (cards[index]) {
        slot.insertAdjacentHTML('beforebegin', cards[index]);
        rendered += 1;
      }
      slot.remove();
    });
    return rendered;
  };

  return { handleFromProduct, getApiKey, renderSkeletons, renderCards };
})();
